import { Controller } from "@hotwired/stimulus"
import * as maplibregl from "maplibre-gl"
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url"

// Generic "many points on a world map" controller (CONEK-7691 / T9b): clustered
// GeoJSON points, click a cluster to zoom in, click a point for a popup. The
// staff device map is the first user; nothing here knows about devices — the
// server decides properties (label, kindLabel, tenant, site, online, lastSeen,
// href, kind).
//
// Kept separate from maps/route_controller.js (an ordered-stops-plus-polyline
// view) so neither can regress the other. The worker wiring is the same
// documented v6 requirement — see route_controller.js for the why.
maplibregl.setWorkerUrl(maplibreWorkerUrl)

const SOURCE_ID = "points"
// Fallbacks only: the server owns the palette (data-...-palette-value) so the
// legend and the map layers can never disagree.
const DEFAULT_PALETTE = {
  gateway: "#2563eb", // blue-600
  rackeye: "#16a34a", // green-600
  offline: "#9ca3af",
  cluster: "#6366f1",
}
const DEFAULT_COLOR = "#6366f1"

const round4 = (value) => Math.round(value * 10000) / 10000

export default class extends Controller {
  static values = {
    styleUrl: String,
    apiKey: String,
    geojson: Object,
    palette: Object,
    bboxInputSelector: String,
    formSelector: String,
    attribution: String,
    clusterRadius: { type: Number, default: 50 },
  }

  static targets = ["canvas", "fallback"]

  get _colors() {
    return { ...DEFAULT_PALETTE, ...(this.paletteValue || {}) }
  }

  connect() {
    this._watchFilterChanges()
    if (!this.apiKeyValue || !this.styleUrlValue) {
      this._showFallback(true)
      return
    }
    this._showFallback(false)

    const apiKey = this.apiKeyValue
    const features = this._validFeatures(this.geojsonValue)
    this._map = new maplibregl.Map({
      container: this.hasCanvasTarget ? this.canvasTarget : this.element,
      style: this.styleUrlValue,
      center: features.length > 0 ? features[0].geometry.coordinates : [0, 20],
      zoom: features.length > 0 ? 3 : 1.5,
      attributionControl: false,
      transformRequest: (url) => {
        if (url.includes("amazonaws.com")) {
          const separator = url.includes("?") ? "&" : "?"
          return { url: `${url}${separator}key=${apiKey}` }
        }
        return { url }
      },
    })

    // HERE attribution is mandatory under the AWS Service Terms (§82.10): the
    // compact control collapses it to an "i" but never removes it.
    this._map.addControl(
      new maplibregl.AttributionControl({
        compact: true,
        customAttribution: this.attributionValue || "© HERE",
      }),
      "bottom-right",
    )
    this._map.addControl(new maplibregl.NavigationControl(), "top-right")

    this._map.on("load", () => {
      this._loaded = true
      this._addLayers()
      this._fitToPoints(features)
    })
  }

  // The applied bbox belongs to the filters it was applied with. The hidden bbox
  // field is pre-filled from the applied search, so without this a user who then
  // changes the tenant or the kind and presses Apply would silently keep the OLD
  // area (only the Reset link cleared it). Any change to a filter in the form
  // starts a new search: the bbox is cleared, and only "Search this area" (which
  // writes it from the live viewport) puts one back.
  _watchFilterChanges() {
    const input = this.bboxInputSelectorValue
      ? document.querySelector(this.bboxInputSelectorValue)
      : null
    const form = this.formSelectorValue
      ? document.querySelector(this.formSelectorValue)
      : null
    if (!input || !form) return
    this._filterForm = form
    this._onFilterChange = (event) => {
      if (event.target === input) return
      input.value = ""
    }
    form.addEventListener("change", this._onFilterChange)
  }

  // "Search this area": write the viewport into the form's bbox input as
  // "west,south,east,north" and submit the (GET) filter form. Only numbers we
  // computed go into the input — never user text.
  searchArea() {
    if (!this._map) return
    const input = this.bboxInputSelectorValue
      ? document.querySelector(this.bboxInputSelectorValue)
      : null
    const form = this.formSelectorValue
      ? document.querySelector(this.formSelectorValue)
      : null
    if (!input || !form) return
    input.value = this._viewportBbox().join(",")
    if (typeof form.requestSubmit === "function") form.requestSubmit()
    else form.submit()
  }

  // West/east wrapped into [-180, 180] (a zoomed-out or panned map reports
  // longitudes beyond it); a viewport spanning the whole world is the whole
  // world. west > east is a legal antimeridian-crossing box for the server.
  _viewportBbox() {
    const bounds = this._map.getBounds()
    const clamp = (value, limit) => Math.max(-limit, Math.min(limit, value))
    const south = clamp(bounds.getSouth(), 90)
    const north = clamp(bounds.getNorth(), 90)
    const rawWest = bounds.getWest()
    const rawEast = bounds.getEast()
    if (rawEast - rawWest >= 360) return [-180, south, 180, north].map(round4)
    const wrap = (lng) => ((((lng + 180) % 360) + 360) % 360) - 180
    return [wrap(rawWest), south, wrap(rawEast), north].map(round4)
  }

  disconnect() {
    if (this._filterForm && this._onFilterChange) {
      this._filterForm.removeEventListener("change", this._onFilterChange)
    }
    this._filterForm = null
    this._onFilterChange = null
    this._closePopup()
    if (this._map) {
      this._map.remove()
      this._map = null
    }
    this._loaded = false
  }

  // A Turbo-driven refresh swaps the data-*-geojson-value attribute on the same
  // element; push it into the live source instead of rebuilding the map (and
  // losing the viewport). Before `load` the initial data is used, so ignore.
  geojsonValueChanged() {
    if (!this._map || !this._loaded) return
    const source = this._map.getSource(SOURCE_ID)
    if (!source) return
    source.setData({
      type: "FeatureCollection",
      features: this._validFeatures(this.geojsonValue),
    })
  }

  // Features with real, finite, in-range [lng, lat]. null/""/NaN would poison
  // maplibre bounds and drag the viewport to [0,0] (same trap as the route map).
  _validFeatures(collection) {
    const features = (collection && collection.features) || []
    return features.filter((feature) => {
      const coords = feature && feature.geometry && feature.geometry.coordinates
      if (!Array.isArray(coords) || coords.length < 2) return false
      const [lng, lat] = coords
      if (lng === null || lat === null || lng === "" || lat === "") return false
      return (
        Number.isFinite(Number(lng)) &&
        Number.isFinite(Number(lat)) &&
        Math.abs(Number(lng)) <= 180 &&
        Math.abs(Number(lat)) <= 90
      )
    })
  }

  _addLayers() {
    this._map.addSource(SOURCE_ID, {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: this._validFeatures(this.geojsonValue),
      },
      cluster: true,
      clusterRadius: this.clusterRadiusValue,
      clusterMaxZoom: 14,
    })

    this._map.addLayer({
      id: "points-clusters",
      type: "circle",
      source: SOURCE_ID,
      filter: ["has", "point_count"],
      paint: {
        "circle-color": this._colors.cluster,
        "circle-radius": ["step", ["get", "point_count"], 16, 10, 22, 50, 28],
        "circle-stroke-width": 2,
        "circle-stroke-color": "#ffffff",
      },
    })

    this._map.addLayer({
      id: "points-cluster-count",
      type: "symbol",
      source: SOURCE_ID,
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["to-string", ["get", "point_count"]],
        "text-size": 12,
        "text-font": ["Noto Sans Bold"],
      },
      paint: { "text-color": "#ffffff" },
    })

    this._map.addLayer({
      id: "points-unclustered",
      type: "circle",
      source: SOURCE_ID,
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-radius": 8,
        "circle-color": [
          "case",
          ["==", ["get", "online"], false],
          this._colors.offline,
          ["==", ["get", "kind"], "gateway"],
          this._colors.gateway,
          ["==", ["get", "kind"], "rackeye"],
          this._colors.rackeye,
          DEFAULT_COLOR,
        ],
        "circle-stroke-width": 2,
        "circle-stroke-color": "#ffffff",
      },
    })

    this._map.on("click", "points-clusters", (event) =>
      this._zoomIntoCluster(event),
    )
    this._map.on("click", "points-unclustered", (event) =>
      this._openPopup(event),
    )
    for (const layer of ["points-clusters", "points-unclustered"]) {
      this._map.on("mouseenter", layer, () => this._setCursor("pointer"))
      this._map.on("mouseleave", layer, () => this._setCursor(""))
    }
  }

  _setCursor(value) {
    if (this._map) this._map.getCanvas().style.cursor = value
  }

  _fitToPoints(features) {
    if (features.length === 0) return
    const bounds = new maplibregl.LngLatBounds()
    features.forEach((feature) => bounds.extend(feature.geometry.coordinates))
    this._map.resize()
    this._map.fitBounds(bounds, { padding: 48, maxZoom: 10 })
  }

  async _zoomIntoCluster(event) {
    const feature = event.features && event.features[0]
    if (!feature) return
    const source = this._map.getSource(SOURCE_ID)
    const zoom = await source.getClusterExpansionZoom(
      feature.properties.cluster_id,
    )
    this._map.easeTo({ center: feature.geometry.coordinates, zoom })
  }

  _openPopup(event) {
    const feature = event.features && event.features[0]
    if (!feature) return
    this._closePopup()
    this._popup = new maplibregl.Popup({ offset: 12, maxWidth: "280px" })
      .setLngLat(feature.geometry.coordinates.slice())
      .setDOMContent(this._popupContent(feature.properties))
      .addTo(this._map)
  }

  _closePopup() {
    if (this._popup) {
      this._popup.remove()
      this._popup = null
    }
  }

  // Built with textContent only — device and tenant names are operator-entered
  // free text and must never be parsed as HTML.
  _popupContent(props) {
    const root = document.createElement("div")
    root.className = "text-sm"
    root.dataset.testid = "map-popup"

    const title = document.createElement("div")
    title.className = "font-semibold"
    title.textContent = props.label || ""
    root.appendChild(title)

    const lines = [props.kindLabel, props.tenant, props.site]
    for (const line of lines) {
      if (!line) continue
      const el = document.createElement("div")
      el.className = "text-muted-foreground"
      el.textContent = line
      root.appendChild(el)
    }

    // Only same-origin relative links: the server builds href, but a popup must
    // never become a javascript:/external-URL vector if data is ever wrong.
    if (typeof props.href === "string" && /^\/(?!\/)/.test(props.href)) {
      const link = document.createElement("a")
      link.className = "underline"
      link.href = props.href
      link.textContent = this.element.dataset.popupLinkLabel || props.href
      link.dataset.testid = "map-popup-link"
      root.appendChild(link)
    }
    return root
  }

  _showFallback(visible) {
    if (this.hasFallbackTarget) this.fallbackTarget.hidden = !visible
    if (this.hasCanvasTarget) this.canvasTarget.hidden = visible
  }
}
